import { Fragment, type ReactNode } from "react";
import { SectionHead } from "./ui";

export type Column<T> = {
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** The column that names the row: takes the spare width, and on a phone its own line at the top. */
  primary?: boolean;
  align?: "right";
  /** Left out on a phone, where the row stacks (an empty heading's actions column, a detail the primary cell already says). */
  hideOnPhone?: boolean;
  /** Extra classes on the column's cells. */
  className?: string;
};

/**
 * The one table: column headings over rows in a white card, the same on every page.
 * Below the width of a laptop (a phone, a tablet, a narrow window) the headings go and each row stacks: the primary cell first, the others after it, wrapping.
 */
export function Table<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowClassName,
  detail,
  empty,
  title,
  hint,
  action,
  filters,
  className = "",
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string;
  /** A full-width part under a row, such as what an opened row shows. */
  detail?: (row: T) => ReactNode;
  empty?: ReactNode;
  title?: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  /** A search box or filters, between the title and the box. */
  filters?: ReactNode;
  className?: string;
}) {
  const phone = (c: Column<T>) => (c.hideOnPhone ? "max-lg:hidden" : c.primary ? "max-lg:w-full" : "max-lg:text-xs");
  return (
    <section className={`min-w-0 ${className}`}>
      <SectionHead title={title} hint={hint} action={action} />
      {filters && <div className="mb-3">{filters}</div>}
      <div className="card scroll-thin overflow-x-auto">
        <table className="w-full text-sm max-lg:block">
          <thead className="text-left text-xs text-muted max-lg:hidden">
            <tr>
              {columns.map((c, i) => (
                <th key={i} className={`px-4 py-2.5 font-medium whitespace-nowrap ${c.align === "right" ? "text-right" : ""}`}>{c.header}</th>
              ))}
            </tr>
          </thead>
          <tbody className="max-lg:block">
            {rows.map((row) => {
              const open = onRowClick && (() => onRowClick(row));
              const more = detail?.(row);
              return (
                <Fragment key={rowKey(row)}>
                  <tr
                    tabIndex={open ? 0 : undefined}
                    onClick={open}
                    onKeyDown={open && ((e) => e.key === "Enter" && e.target === e.currentTarget && open())}
                    className={`border-t border-line align-middle max-lg:flex max-lg:flex-wrap max-lg:items-center max-lg:gap-x-3 max-lg:gap-y-1 max-lg:px-4 max-lg:py-3 max-lg:first:border-t-0 ${
                      open ? "cursor-pointer transition hover:bg-fill-subtle focus-visible:bg-fill-subtle focus-visible:outline-none" : ""
                    } ${rowClassName?.(row) ?? ""}`}
                  >
                    {columns.map((c, i) => (
                      <td
                        key={i}
                        className={`px-4 py-3 max-lg:max-w-full max-lg:min-w-0 max-lg:p-0 max-lg:[overflow-wrap:anywhere] ${c.primary ? "lg:w-full lg:max-w-0 lg:min-w-48" : "lg:whitespace-nowrap"} ${c.align === "right" ? "text-right" : ""} ${phone(c)} ${c.className ?? ""}`}
                      >
                        {c.cell(row)}
                      </td>
                    ))}
                  </tr>
                  {more && (
                    <tr className="max-lg:block">
                      <td colSpan={columns.length} className="bg-fill-subtle px-4 py-3 max-lg:block">{more}</td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {rows.length === 0 && empty !== undefined && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-muted max-lg:block">{empty}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
